// Section « Recherche (Elasticsearch) » de la page Metrics : combien de requêtes
// par jour, et combien de temps elles prennent.
//
// Deux natures d'appel sont suivies séparément : la recherche complète et
// l'autosuggestion du champ de recherche. Une suggestion est bien plus rapide,
// les mélanger masquerait un ralentissement de la vraie recherche.
//
// Les compteurs sont écrits côté serveur par un service de fond ; le visiteur du
// site public n'attend jamais l'enregistrement de la statistique.
import React, { useMemo } from "react";
import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import { useSearchUsage, SearchUsage } from "../hooks/useSearchUsage";

const nombre = new Intl.NumberFormat("fr-FR");

/** « 128 ms », « 1,4 s » : au-delà de la seconde, la milliseconde ne parle plus. */
export const formatDuree = (ms: number): string => {
	if (!Number.isFinite(ms) || ms <= 0) return "0 ms";
	if (ms >= 1000) return `${(ms / 1000).toLocaleString("fr-FR", { maximumFractionDigits: 2 })} s`;
	return `${ms.toLocaleString("fr-FR", { maximumFractionDigits: ms < 10 ? 1 : 0 })} ms`;
};

/** Le seuil au-delà duquel une moyenne mérite d'être regardée de près. */
const SEUIL_LENT_MS = 400;

const jourCourt = (iso: string): string => {
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return iso;
	return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
};

interface SearchUsageSectionProps {
	/** Données déjà chargées par la page hôte (ex. /search-metrics) : si fournies,
	 * la section ne fait AUCUN appel réseau. */
	usage?: SearchUsage | null;
	loading?: boolean;
	error?: string | null;
}

const SearchUsageSection: React.FC<SearchUsageSectionProps> = (props) => {
	const externe = props.usage !== undefined;
	const interne = useSearchUsage(14, 60000, !externe);
	const usage = externe ? props.usage : interne.usage;
	const loading = externe ? props.loading ?? false : interne.loading;
	const error = externe ? props.error ?? null : interne.error;

	const jours = usage?.days ?? [];
	const categories = useMemo(() => jours.map((j) => jourCourt(j.date)), [jours]);

	const totalRecherches = jours.reduce((n, j) => n + j.search.requests, 0);
	const totalSuggestions = jours.reduce((n, j) => n + j.suggest.requests, 0);
	const totalEchecs = jours.reduce((n, j) => n + j.search.failures + j.suggest.failures, 0);
	// Moyenne de la fenêtre, pondérée par le nombre de requêtes : une journée à
	// 3 requêtes ne doit pas peser autant qu'une journée à 3 000.
	const moyenneFenetre = useMemo(() => {
		const total = jours.reduce((n, j) => n + j.search.requests, 0);
		if (total === 0) return 0;
		return jours.reduce((n, j) => n + j.search.averageMs * j.search.requests, 0) / total;
	}, [jours]);
	const pireJour = jours.reduce((max, j) => Math.max(max, j.search.maxMs), 0);
	const aujourdhui = usage?.today;

	const optionsCommunes = (formatteur: (v: number) => string): ApexOptions => ({
		chart: { type: "area", height: 220, toolbar: { show: false }, zoom: { enabled: false } },
		stroke: { width: 3, curve: "smooth" },
		fill: {
			type: "gradient",
			gradient: { shadeIntensity: 0.3, opacityFrom: 0.35, opacityTo: 0.05, stops: [0, 90, 100] },
		},
		dataLabels: { enabled: false },
		legend: { position: "top", horizontalAlign: "right", labels: { colors: "#64748b" } },
		grid: { borderColor: "#e2e8f0", strokeDashArray: 3 },
		xaxis: {
			categories,
			// Une étiquette sur deux suffit sur quatorze jours : au-delà, elles se
			// chevauchent et deviennent illisibles.
			tickAmount: 7,
			labels: { style: { colors: "#94a3b8" }, rotate: 0, hideOverlappingLabels: true },
			axisBorder: { color: "#e2e8f0" },
			axisTicks: { color: "#e2e8f0" },
		},
		yaxis: { labels: { formatter: formatteur, style: { colors: "#94a3b8" } }, min: 0 },
		tooltip: { y: { formatter: formatteur } },
	});

	const cartes = [
		{ libelle: "Recherches (14 jours)", valeur: nombre.format(totalRecherches), detail: `et ${nombre.format(totalSuggestions)} autosuggestions` },
		{
			libelle: "Temps moyen par recherche",
			valeur: formatDuree(moyenneFenetre),
			detail: moyenneFenetre > SEUIL_LENT_MS ? "au-dessus du seuil de 400 ms" : "sous le seuil de 400 ms",
			alerte: moyenneFenetre > SEUIL_LENT_MS,
		},
		{
			libelle: "Aujourd'hui",
			valeur: nombre.format(aujourdhui?.search.requests ?? 0),
			detail: `moyenne ${formatDuree(aujourdhui?.search.averageMs ?? 0)}`,
		},
		{
			libelle: "Requête la plus lente",
			valeur: formatDuree(pireJour),
			detail: totalEchecs > 0 ? `${nombre.format(totalEchecs)} appel(s) sans réponse` : "aucun appel sans réponse",
			alerte: totalEchecs > 0,
		},
	];

	return (
		<section className="space-y-4 rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
			<header>
				<p className="text-sm font-medium uppercase tracking-wide text-slate-500">Recherche (Elasticsearch)</p>
				<p className="mt-1 text-xs text-slate-400">
					Appels réellement servis par le moteur, mesurés sur les quatorze derniers jours. La mesure est
					enregistrée après coup : elle n'ajoute aucun temps d'attente au visiteur.
				</p>
			</header>

			{error ? (
				<div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
			) : null}

			{loading && !usage ? (
				<div className="grid gap-6 md:grid-cols-2">
					{Array.from({ length: 2 }).map((_, i) => (
						<div key={i} className="h-64 animate-pulse rounded-2xl border border-slate-100 bg-slate-50" />
					))}
				</div>
			) : (
				<>
					<div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
						{cartes.map((carte) => (
							<div key={carte.libelle} className="rounded-2xl border border-slate-100 bg-slate-50/60 p-4">
								<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{carte.libelle}</p>
								<p className="mt-1.5 text-2xl font-bold tabular-nums text-slate-900">{carte.valeur}</p>
								<p className={`mt-1 text-xs ${carte.alerte ? "font-semibold text-amber-700" : "text-slate-400"}`}>
									{carte.detail}
								</p>
							</div>
						))}
					</div>

					<div className="grid gap-6 md:grid-cols-2">
						<div className="rounded-2xl border border-slate-100 p-4">
							<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Requêtes par jour</p>
							<Chart
								options={{ ...optionsCommunes((v) => nombre.format(v)), colors: ["#0ea5e9", "#94a3b8"] }}
								series={[
									{ name: "Recherches", data: jours.map((j) => j.search.requests) },
									{ name: "Autosuggestions", data: jours.map((j) => j.suggest.requests) },
								]}
								type="area"
								height={220}
							/>
						</div>
						<div className="rounded-2xl border border-slate-100 p-4">
							<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
								Temps moyen par requête
							</p>
							<Chart
								options={{ ...optionsCommunes(formatDuree), colors: ["#6366f1", "#a5b4fc"] }}
								series={[
									{ name: "Recherches", data: jours.map((j) => Math.round(j.search.averageMs)) },
									{ name: "Autosuggestions", data: jours.map((j) => Math.round(j.suggest.averageMs)) },
								]}
								type="area"
								height={220}
							/>
						</div>
					</div>
				</>
			)}
		</section>
	);
};

export default SearchUsageSection;
